import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
    Alert,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PaymentIcon from "@mui/icons-material/Payment";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import Popup from "../../../components/Popup";
import OnekhusaPaymentDialog from "../../../components/OnekhusaPaymentDialog";
import { ENABLE_FEE_ONLINE_PAYMENT } from "../../../config/featureFlags";
import {
    FinancePageContainer,
    FinancePageHeader,
    FinancePanel,
    FinanceHeadCell,
    FinanceTableRow,
    FinanceStatTile,
    financeTableContainerSx,
} from "./financeUi";
import { getAllFeeRecords, getStudentFeeRecords, recordPayment } from "../../../redux/feeRelated/feeHandle";
import { underFeeControl } from "../../../redux/feeRelated/feeSlice";
import { getAllSclasses } from "../../../redux/sclassRelated/sclassHandle";
import {
    buildFinanceRecordsQuery,
    FinanceRecordsScopeAlert,
    FinanceRecordsScopeTabs,
    FINANCE_RECORDS_SCOPES,
} from "../../../components/finance/FinanceRecordsScopeTabs";
import { fetchSchoolCurrentTerm } from "../../../utils/currentTermUtils";
import { getSchoolIdForUser } from "../../../utils/userContext";
import { feeRecordPath } from "../../../utils/financePaths";

const TERMS = ["Term 1", "Term 2", "Term 3", "Full Year"];
const STATUS_OPTIONS = ["All", "Unpaid", "Partial", "Paid", "Waived"];
const PAYMENT_METHODS = [
    "Cash",
    "Airtel Money",
    "TNM Mpamba",
    "Bank Transfer",
    "Cheque",
    "Other",
];

const y = new Date().getFullYear();
const ACADEMIC_YEARS = [`${y - 1}/${y}`, `${y}/${y + 1}`, `${y + 1}/${y + 2}`];

const formatMWK = (n) => "MWK " + Number(n || 0).toLocaleString();

function feeErrorMessage(err) {
    if (!err) return "Request failed";
    if (typeof err === "string") return err;
    if (typeof err.response?.data?.message === "string") return err.response.data.message;
    if (err.message) return err.message;
    return "Request failed";
}

function statusChipColor(status) {
    const s = String(status || "");
    if (s === "Unpaid") return "error";
    if (s === "Partial") return "warning";
    if (s === "Paid") return "success";
    if (s === "Waived") return "info";
    if (s === "Overpaid") return "secondary";
    return "default";
}

function balanceColor(balance) {
    const b = Number(balance);
    if (b > 0) return "error.main";
    if (b === 0) return "success.main";
    return "secondary.main";
}

const FeeCollection = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser } = useSelector((s) => s.user);
    const { feeRecords, loading, response, error, statestatus } = useSelector((s) => s.fee);
    const { sclassesList } = useSelector((s) => s.sclass);

    const schoolId = getSchoolIdForUser(currentUser);

    const [search, setSearch] = useState("");
    const [filterClass, setFilterClass] = useState("");
    const [filterTerm, setFilterTerm] = useState("");
    const [filterYear, setFilterYear] = useState("");
    const [filterStatus, setFilterStatus] = useState("All");
    const [recordsScope, setRecordsScope] = useState(FINANCE_RECORDS_SCOPES.CURRENT);
    const [currentTermLabel, setCurrentTermLabel] = useState("");

    const filterRef = useRef({
        filterClass: "",
        filterTerm: "",
        filterYear: "",
        filterStatus: "All",
        recordsScope: FINANCE_RECORDS_SCOPES.CURRENT,
    });
    filterRef.current = { filterClass, filterTerm, filterYear, filterStatus, recordsScope };

    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");

    const [payOpen, setPayOpen] = useState(false);
    const [targetRecord, setTargetRecord] = useState(null);
    const [payAmount, setPayAmount] = useState("");
    const [payMethod, setPayMethod] = useState("Cash");
    const [payDate, setPayDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [payRef, setPayRef] = useState("");
    const [payReceivedBy, setPayReceivedBy] = useState(currentUser?.name || "");
    const [payNotes, setPayNotes] = useState("");

    const [onekhusaDialog, setOnekhusaDialog] = useState(false);
    const [selectedFeeRecord, setSelectedFeeRecord] = useState(null);

    const rawRecords = useMemo(
        () => (Array.isArray(feeRecords) ? feeRecords : []),
        [feeRecords]
    );

    const loadRecords = useCallback(() => {
        if (!schoolId) return;
        const {
            filterClass: fc,
            filterTerm: ft,
            filterYear: fy,
            filterStatus: fs,
            recordsScope: scope,
        } = filterRef.current;
        const filters = buildFinanceRecordsQuery({
            recordsScope: scope,
            term: ft,
            academicYear: fy,
            extra: {
                ...(fc ? { sclassId: fc } : {}),
                ...(fs && fs !== "All" ? { status: fs } : {}),
            },
        });
        dispatch(getAllFeeRecords(schoolId, filters));
    }, [dispatch, schoolId]);

    useEffect(() => {
        if (!schoolId) return;
        dispatch(getAllSclasses(schoolId, "Sclass"));
        fetchSchoolCurrentTerm({ schoolId, includeFees: false })
            .then((info) => setCurrentTermLabel(info?.label || ""))
            .catch(() => setCurrentTermLabel(""));
    }, [schoolId, dispatch]);

    useEffect(() => {
        if (!schoolId) return;
        loadRecords();
    }, [schoolId, recordsScope, loadRecords]);

    useEffect(() => {
        setPayReceivedBy((prev) => (currentUser?.name ? currentUser.name : prev));
    }, [currentUser?.name]);

    useEffect(() => {
        if (statestatus === "added") {
            setMessage("Completed successfully!");
            setShowPopup(true);
            dispatch(underFeeControl());
            loadRecords();
            setPayOpen(false);
            setTargetRecord(null);
        } else if (statestatus === "failed") {
            const msg =
                typeof response === "string" && response
                    ? response
                    : feeErrorMessage(error);
            setMessage(msg || "Request failed");
            setShowPopup(true);
            dispatch(underFeeControl());
        }
    }, [statestatus, response, error, dispatch, loadRecords]);

    const filteredRecords = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rawRecords;
        return rawRecords.filter((r) => {
            const name = String(r.student?.name || "").toLowerCase();
            const roll = String(r.student?.rollNum ?? "").toLowerCase();
            return name.includes(q) || roll.includes(q);
        });
    }, [rawRecords, search]);

    const stats = useMemo(() => {
        if (!filteredRecords.length) return null;
        let totalDue = 0;
        let totalPaid = 0;
        let outstanding = 0;
        for (const r of filteredRecords) {
            totalDue += Number(r.totalDue) || 0;
            totalPaid += Number(r.totalPaid) || 0;
            outstanding += Number(r.balance) || 0;
        }
        return {
            count: filteredRecords.length,
            totalDue,
            totalPaid,
            outstanding,
        };
    }, [filteredRecords]);

    const openPayment = (r) => {
        setTargetRecord(r);
        const bal = Number(r.balance) || 0;
        setPayAmount(String(bal > 0 ? bal : ""));
        setPayMethod("Cash");
        setPayDate(new Date().toISOString().slice(0, 10));
        setPayRef("");
        setPayReceivedBy(currentUser?.name || "");
        setPayNotes("");
        setPayOpen(true);
    };

    const handleRecordPayment = () => {
        if (!targetRecord) return;
        const rid = targetRecord._id || targetRecord.id;
        const amt = Number(payAmount);
        if (!Number.isFinite(amt) || amt <= 0) return;
        dispatch(
            recordPayment(rid, {
                amount: amt,
                paymentMethod: payMethod,
                paymentDate: payDate,
                referenceNumber: payRef,
                collectorName: payReceivedBy,
                collectedById: currentUser._id,
                notes: payNotes,
                schoolId,
            })
        );
    };

    const recordRowId = (r) => r._id || r.id;

    return (
        <FinancePageContainer>
            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />

            <FinanceRecordsScopeTabs value={recordsScope} onChange={setRecordsScope} />
            <FinanceRecordsScopeAlert scope={recordsScope} currentTermLabel={currentTermLabel} />

            <FinancePageHeader
                title="Fee collection"
                subtitle="Search and filter fee records, then record payments or open a student’s ledger."
                action={
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={loadRecords}
                        disabled={loading || !schoolId}
                    >
                        Refresh
                    </Button>
                }
            />

            <FinancePanel
                title="Filters"
                description="Narrow the list, then Apply. Search matches student name or roll number."
                sx={{ mb: 2 }}
                contentSx={{ p: { xs: 2, sm: 2.5 } }}
            >
                <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Search"
                            placeholder="Name or roll no."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="fc-class">Class</InputLabel>
                            <Select
                                labelId="fc-class"
                                label="Class"
                                value={filterClass}
                                onChange={(e) => setFilterClass(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>All Classes</em>
                                </MenuItem>
                                {(Array.isArray(sclassesList) ? sclassesList : []).map((c) => {
                                    const cid = c._id || c.id;
                                    return (
                                        <MenuItem key={cid} value={cid}>
                                            {c.sclassName}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="fc-term">Term</InputLabel>
                            <Select
                                labelId="fc-term"
                                label="Term"
                                value={filterTerm}
                                onChange={(e) => setFilterTerm(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>All Terms</em>
                                </MenuItem>
                                {TERMS.map((t) => (
                                    <MenuItem key={t} value={t}>
                                        {t}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="fc-year">Academic year</InputLabel>
                            <Select
                                labelId="fc-year"
                                label="Academic year"
                                value={filterYear}
                                onChange={(e) => setFilterYear(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>All years</em>
                                </MenuItem>
                                {ACADEMIC_YEARS.map((yr) => (
                                    <MenuItem key={yr} value={yr}>
                                        {yr}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="fc-status">Status</InputLabel>
                            <Select
                                labelId="fc-status"
                                label="Status"
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                            >
                                {STATUS_OPTIONS.map((s) => (
                                    <MenuItem key={s} value={s}>
                                        {s}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={1}>
                        <Button fullWidth variant="contained" onClick={loadRecords} disabled={!schoolId}>
                            Apply
                        </Button>
                    </Grid>
                </Grid>
            </FinancePanel>

            {filteredRecords.length > 0 && stats && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <FinanceStatTile label="Records (filtered)" value={stats.count} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FinanceStatTile label="Total due" value={formatMWK(stats.totalDue)} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FinanceStatTile
                            label="Collected"
                            value={formatMWK(stats.totalPaid)}
                            valueColor="success.main"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FinanceStatTile
                            label="Outstanding"
                            value={formatMWK(stats.outstanding)}
                            valueColor={stats.outstanding > 0 ? "error.main" : "text.primary"}
                        />
                    </Grid>
                </Grid>
            )}

            <FinancePanel
                title="Fee records"
                description={`${filteredRecords.length} row${filteredRecords.length === 1 ? "" : "s"} in the table below.`}
                sx={{ mb: 0 }}
                contentSx={{ p: 0 }}
            >
            <TableContainer sx={financeTableContainerSx}>
                <Table size="medium">
                    <TableHead>
                        <TableRow>
                            <FinanceHeadCell>Student</FinanceHeadCell>
                            <FinanceHeadCell>Roll no</FinanceHeadCell>
                            <FinanceHeadCell>Class</FinanceHeadCell>
                            <FinanceHeadCell>Term / year</FinanceHeadCell>
                            <FinanceHeadCell align="right">Due</FinanceHeadCell>
                            <FinanceHeadCell align="right">Paid</FinanceHeadCell>
                            <FinanceHeadCell align="right">Balance</FinanceHeadCell>
                            <FinanceHeadCell>Status</FinanceHeadCell>
                            <FinanceHeadCell align="right">Actions</FinanceHeadCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && filteredRecords.length === 0 ? (
                            <FinanceTableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                                    <Typography color="text.secondary">Loading records…</Typography>
                                </TableCell>
                            </FinanceTableRow>
                        ) : filteredRecords.length === 0 ? (
                            <FinanceTableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                                    <Typography color="text.secondary">
                                        No fee records match your filters.
                                    </Typography>
                                </TableCell>
                            </FinanceTableRow>
                        ) : (
                            filteredRecords.map((r) => {
                                const rid = recordRowId(r);
                                const term = r.feeStructure?.term ?? r.term ?? "—";
                                const year = r.feeStructure?.academicYear ?? r.academicYear ?? "—";
                                const st = r.status;
                                const bal = Number(r.balance) || 0;
                                const canPay = st !== "Paid" && st !== "Waived";
                                return (
                                    <FinanceTableRow key={rid}>
                                        <TableCell sx={{ fontWeight: 600 }}>{r.student?.name ?? "—"}</TableCell>
                                        <TableCell>{r.student?.rollNum ?? "—"}</TableCell>
                                        <TableCell>{r.sclass?.sclassName ?? "—"}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" lineHeight={1.2}>
                                                {term}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {year}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                            {formatMWK(r.totalDue)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                            {formatMWK(r.totalPaid)}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{
                                                color: balanceColor(bal),
                                                fontWeight: 700,
                                                fontVariantNumeric: "tabular-nums",
                                            }}
                                        >
                                            {formatMWK(bal)}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={st || "—"}
                                                color={statusChipColor(st)}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="View record">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() =>
                                                        navigate(feeRecordPath(location.pathname, r._id || r.id))
                                                    }
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {canPay && (
                                                <>
                                                    <Tooltip title="Record payment">
                                                        <IconButton
                                                            size="small"
                                                            color="success"
                                                            onClick={() => openPayment(r)}
                                                        >
                                                            <PaymentIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {ENABLE_FEE_ONLINE_PAYMENT ? (
                                                        <Tooltip title="Pay via mobile money">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => {
                                                                    setSelectedFeeRecord(r);
                                                                    setOnekhusaDialog(true);
                                                                }}
                                                                sx={{ color: "#e8c97e" }}
                                                            >
                                                                <PhoneAndroidIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : null}
                                                </>
                                            )}
                                        </TableCell>
                                    </FinanceTableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            </FinancePanel>

            <Dialog
                open={payOpen}
                onClose={() => setPayOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle>Record payment</DialogTitle>
                <DialogContent dividers>
                    {targetRecord && (
                        <Stack spacing={2} sx={{ pt: 0.5 }}>
                            <Alert severity="info">
                                <strong>{targetRecord.student?.name ?? "Student"}</strong>
                                {" · "}
                                {targetRecord.sclass?.sclassName ?? "Class"}
                                {" · "}
                                {targetRecord.feeStructure?.term ?? targetRecord.term ?? ""}{" "}
                                {targetRecord.feeStructure?.academicYear ??
                                    targetRecord.academicYear ??
                                    ""}
                                <br />
                                Outstanding:{" "}
                                <strong>{formatMWK(targetRecord.balance)}</strong>
                            </Alert>
                            <TextField
                                required
                                fullWidth
                                size="small"
                                label="Amount"
                                type="number"
                                value={payAmount}
                                onChange={(e) => setPayAmount(e.target.value)}
                                inputProps={{ min: 0, step: "0.01" }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">MWK</InputAdornment>
                                    ),
                                }}
                            />
                            <FormControl fullWidth size="small">
                                <InputLabel id="pm-method">Payment method</InputLabel>
                                <Select
                                    labelId="pm-method"
                                    label="Payment method"
                                    value={payMethod}
                                    onChange={(e) => setPayMethod(e.target.value)}
                                >
                                    {PAYMENT_METHODS.map((m) => (
                                        <MenuItem key={m} value={m}>
                                            {m}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                fullWidth
                                size="small"
                                label="Payment date"
                                type="date"
                                value={payDate}
                                onChange={(e) => setPayDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                label="Reference number"
                                value={payRef}
                                onChange={(e) => setPayRef(e.target.value)}
                                helperText="Mobile money or bank transaction ref"
                            />
                            <TextField
                                fullWidth
                                size="small"
                                label="Received by"
                                value={payReceivedBy}
                                onChange={(e) => setPayReceivedBy(e.target.value)}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                label="Notes"
                                value={payNotes}
                                onChange={(e) => setPayNotes(e.target.value)}
                                multiline
                                minRows={2}
                            />
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPayOpen(false)}>Cancel</Button>
                    {ENABLE_FEE_ONLINE_PAYMENT && targetRecord ? (
                        <Button
                            variant="outlined"
                            startIcon={<PhoneAndroidIcon />}
                            onClick={() => {
                                setSelectedFeeRecord(targetRecord);
                                setPayOpen(false);
                                setOnekhusaDialog(true);
                            }}
                            sx={{ borderColor: "#e8c97e", color: "#e8c97e", ml: 1 }}
                        >
                            Pay via Mobile Money
                        </Button>
                    ) : null}
                    <Button
                        variant="contained"
                        startIcon={<PaymentIcon />}
                        onClick={handleRecordPayment}
                        disabled={
                            loading ||
                            !targetRecord ||
                            !Number(payAmount) ||
                            Number(payAmount) <= 0
                        }
                    >
                        Record Payment
                    </Button>
                </DialogActions>
            </Dialog>

            {ENABLE_FEE_ONLINE_PAYMENT ? (
                <OnekhusaPaymentDialog
                    open={onekhusaDialog}
                    onClose={() => setOnekhusaDialog(false)}
                    feeRecordId={selectedFeeRecord?._id || selectedFeeRecord?.id}
                    studentName={selectedFeeRecord?.student?.name || ""}
                    outstandingBalance={Number(selectedFeeRecord?.balance) || 0}
                    schoolId={schoolId}
                    onPaymentConfirmed={() => {
                        setOnekhusaDialog(false);
                        if (selectedFeeRecord?.studentId) {
                            dispatch(getStudentFeeRecords(selectedFeeRecord.studentId));
                        }
                        loadRecords();
                    }}
                />
            ) : null}
        </FinancePageContainer>
    );
};

export default FeeCollection;
